import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookieConsent.css';
import { CommonImages } from '../images';
import { isBrowserEnvironment, setAnalyticsConsent } from './analytics';

const COOKIE_POLICY_URL = 'https://ecal.com/cookie-policy/';
const CLOSE_BUTTON_CLASS = 'cm__close-btn';
const SETTINGS_BUTTON_CLASS = 'cc-settings-btn';
const FALLBACK_CLOSE_LABEL = 'Reject and close';
const FALLBACK_SETTINGS_LABEL = 'Change cookie settings';
const SETTINGS_ICON = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
const CLOSE_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>';

const cookiePolicyLink = `<a href="${COOKIE_POLICY_URL}" target="_blank" rel="noopener noreferrer">Cookie Policy</a>`;

const GA_COOKIE_NAMES = [/^_ga/, /^_gid$/];

export function getParentDomains(hostname) {
    const labels = hostname.split('.');
    if (labels.length < 3) {
        return [hostname];
    }
    return labels.slice(0, -1).map((_, index) => labels.slice(index).join('.'));
}

function getAnalyticsCookieRules() {
    const hostname = isBrowserEnvironment() ? window.location.hostname : '';
    return getParentDomains(hostname).flatMap((domain) => (
        GA_COOKIE_NAMES.map((name) => ({ name, domain }))
    ));
}

function getDefaultTranslation() {
    const { translations, default: defaultLanguage } = CookieConsent.getConfig('language');
    return translations[defaultLanguage];
}

function getCloseButtonLabel() {
    return getDefaultTranslation()?.consentModal?.closeIconLabel || FALLBACK_CLOSE_LABEL;
}

function getSettingsButtonLabel() {
    return getDefaultTranslation()?.settingsButton?.label || FALLBACK_SETTINGS_LABEL;
}

function getSettingsButton() {
    return document.querySelector(`.${SETTINGS_BUTTON_CLASS}`);
}

function createSettingsButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = SETTINGS_BUTTON_CLASS;
    button.hidden = true;

    const icon = document.createElement('span');
    icon.className = `${SETTINGS_BUTTON_CLASS}__icon`;
    icon.innerHTML = SETTINGS_ICON;

    const label = document.createElement('span');
    label.className = `${SETTINGS_BUTTON_CLASS}__label`;
    label.textContent = getSettingsButtonLabel();

    button.append(icon, label);
    button.addEventListener('click', () => CookieConsent.showPreferences());
    document.body.appendChild(button);
    return button;
}

function showSettingsButton() {
    const button = getSettingsButton() || createSettingsButton();
    button.hidden = false;
}

function hideSettingsButton() {
    const button = getSettingsButton();
    if (button) {
        button.hidden = true;
    }
}

export const pageActions = {
    reload: () => window.location.reload(),
};

let isFirstConsentInProgress = false;

function handleFirstConsent() {
    isFirstConsentInProgress = true;
    if (CookieConsent.acceptedCategory('analytics')) {
        pageActions.reload();
    }
}

function handleConsent() {
    if (!isFirstConsentInProgress) {
        setAnalyticsConsent(CookieConsent.acceptedCategory('analytics'));
    }
    isFirstConsentInProgress = false;
    showSettingsButton();
}

function handleChange({ changedCategories }) {
    if (!changedCategories.includes('analytics')) {
        return;
    }

    if (!CookieConsent.acceptedCategory('analytics')) {
        setAnalyticsConsent(false);
    }
    pageActions.reload();
}

function rejectAndCloseBanner() {
    CookieConsent.hide();
    CookieConsent.acceptCategory([]);
}

function addCloseButton() {
    const modal = document.querySelector('#cc-main .cm');
    if (!modal || modal.querySelector(`.${CLOSE_BUTTON_CLASS}`)) {
        return;
    }

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = CLOSE_BUTTON_CLASS;
    closeButton.setAttribute('aria-label', getCloseButtonLabel());
    closeButton.innerHTML = CLOSE_ICON;
    closeButton.addEventListener('click', rejectAndCloseBanner);
    modal.appendChild(closeButton);
}

const PANE_HIDDEN_CLASS = 'pm__pane--hidden';

function getPreferencePanes(modal) {
    return [
        modal.querySelector('.pm__body > .pm__section'),
        ...modal.querySelectorAll('.pm__section--toggle'),
    ].filter(Boolean);
}

function getPaneTitle(pane) {
    return pane.querySelector('.pm__section-title')?.textContent.trim() || '';
}

function selectTab(tabs, panes, selectedIndex) {
    tabs.forEach((tab, index) => {
        const isSelected = index === selectedIndex;
        tab.classList.toggle('is-active', isSelected);
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
    });
    panes.forEach((pane, index) => {
        pane.classList.toggle(PANE_HIDDEN_CLASS, index !== selectedIndex);
    });
}

function getNextTabIndex(key, currentIndex, total) {
    if (key === 'ArrowDown' || key === 'ArrowRight') {
        return (currentIndex + 1) % total;
    }
    if (key === 'ArrowUp' || key === 'ArrowLeft') {
        return (currentIndex - 1 + total) % total;
    }
    return -1;
}

function createTabList(panes) {
    const tabList = document.createElement('div');
    tabList.className = 'pm__tabs';
    tabList.setAttribute('role', 'tablist');
    tabList.setAttribute('aria-orientation', 'vertical');

    const tabs = panes.map((pane, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'pm__tab';
        tab.id = `pm-tab-${index}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', `pm-pane-${index}`);
        tab.textContent = getPaneTitle(pane);
        pane.id = `pm-pane-${index}`;
        pane.setAttribute('role', 'tabpanel');
        pane.setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => selectTab(tabs, panes, index));
        tab.addEventListener('keydown', (event) => {
            const nextIndex = getNextTabIndex(event.key, index, tabs.length);
            if (nextIndex === -1) {
                return;
            }
            event.preventDefault();
            selectTab(tabs, panes, nextIndex);
            tabs[nextIndex].focus();
        });
        return tab;
    });

    tabList.append(...tabs);
    selectTab(tabs, panes, 0);
    return tabList;
}

function addPreferencesBrand(modal, panes) {
    if (modal.querySelector('.pm__brand')) {
        return;
    }

    const brand = document.createElement('div');
    brand.className = 'pm__brand';

    const logo = document.createElement('img');
    logo.className = 'pm__brand-logo';
    logo.src = CommonImages.ECAL_LOGO;
    logo.alt = 'ECAL';

    const policyLink = document.createElement('a');
    policyLink.className = 'pm__brand-link';
    policyLink.href = COOKIE_POLICY_URL;
    policyLink.target = '_blank';
    policyLink.rel = 'noopener noreferrer';
    policyLink.textContent = 'Cookie Policy';

    brand.append(logo, createTabList(panes), policyLink);
    modal.appendChild(brand);
}

function keepToggleSectionsExpanded(modal) {
    modal.querySelectorAll('.pm__section--toggle').forEach((section) => {
        const title = section.querySelector('button.pm__section-title');
        section.classList.add('is-expanded');
        title?.setAttribute('aria-expanded', 'true');
        title?.setAttribute('tabindex', '-1');
    });
}

function switchOnAllToggles(modal) {
    modal.querySelectorAll('.section__toggle:not(:disabled)').forEach((toggle) => {
        if (!toggle.checked) {
            toggle.checked = true;
            toggle.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });
}

function bindEnableAllToSwitches(modal) {
    if (modal.dataset.enableAllBound) {
        return;
    }

    modal.dataset.enableAllBound = 'true';
    modal.addEventListener('click', (event) => {
        if (!event.target.closest('.pm__footer [data-role="all"]')) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        switchOnAllToggles(modal);
    }, true);
}

function decoratePreferencesModal() {
    const modal = document.querySelector('#cc-main .pm');
    if (!modal) {
        return;
    }

    keepToggleSectionsExpanded(modal);
    addPreferencesBrand(modal, getPreferencePanes(modal));
    bindEnableAllToSwitches(modal);
}

function handleModalShow({ modalName }) {
    if (modalName === 'consentModal') {
        hideSettingsButton();
        addCloseButton();
    }
    if (modalName === 'preferencesModal') {
        decoratePreferencesModal();
    }
}

export const cookieConsentConfig = {
    guiOptions: {
        consentModal: { layout: 'bar inline', position: 'bottom', equalWeightButtons: true },
        preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
            autoClear: {
                cookies: getAnalyticsCookieRules(),
            },
        },
    },
    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    label: 'Cookie consent',
                    description: `We use cookies to enhance your browsing experience and analyse our traffic. By clicking "Accept", you consent to our use of analytics cookies. For more information, please read our ${cookiePolicyLink}.`,
                    acceptAllBtn: 'Accept',
                    acceptNecessaryBtn: 'Reject',
                    showPreferencesBtn: 'Settings',
                    closeIconLabel: 'Reject analytics cookies and close',
                },
                settingsButton: { label: FALLBACK_SETTINGS_LABEL },
                preferencesModal: {
                    title: 'Cookie settings',
                    acceptAllBtn: 'Enable All',
                    savePreferencesBtn: 'Save Settings',
                    closeIconLabel: 'Close',
                    sections: [
                        {
                            title: 'Privacy Overview',
                            description: 'This website uses cookies so that we can provide you with the best user experience possible. Cookie information is stored in your browser and performs functions such as recognising you when you return to our website and helping our team to understand which sections of the website you find most interesting and useful.',
                        },
                        {
                            title: 'Strictly Necessary Cookies',
                            description: 'Strictly Necessary Cookies should be enabled at all times so that we can save your preferences for cookie settings.',
                            linkedCategory: 'necessary',
                        },
                        {
                            title: '3rd Party Cookies',
                            description: 'This website uses Google Analytics to collect anonymous information such as the number of visitors to the site, and the most popular pages.<br><br>Keeping this cookie enabled helps us to improve our website.',
                            linkedCategory: 'analytics',
                        },
                    ],
                },
            },
        },
    },
    onModalShow: handleModalShow,
    onFirstConsent: handleFirstConsent,
    onConsent: handleConsent,
    onChange: handleChange,
};

export function initializeCookieConsent() {
    if (!isBrowserEnvironment()) {
        return;
    }

    CookieConsent.run(cookieConsentConfig);
}
